import { createContext, useCallback, useContext, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { CurentiaDevice, Snapshot } from './lib/device';
import { messageFor } from './lib/errors';

const DeviceContext = createContext<CurentiaDevice | null>(null);

export function DeviceProvider({ device, children }: { device: CurentiaDevice; children: ReactNode }) {
  return <DeviceContext.Provider value={device}>{children}</DeviceContext.Provider>;
}

export function useDevice(): CurentiaDevice {
  const device = useContext(DeviceContext);
  if (!device) throw new Error('DeviceProvider is missing');
  return device;
}

export function useSnapshot(): Snapshot {
  const device = useDevice();
  return useSyncExternalStore(device.subscribe, device.getSnapshot);
}

/** Runs a screen action with a busy flag and a message for any error. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, error, setError, run };
}
